import { useState, useEffect } from "react";
import api, { formatCurrency, formatDate } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, Search, FileText, Receipt, CreditCard, ArrowUpRight, ArrowDownLeft, Download } from "lucide-react";

export default function VATAuditTrailPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [vatType, setVatType] = useState("");

  const fetchEntries = () => {
    const params = {};
    if (periodStart) params.period_start = periodStart;
    if (periodEnd) params.period_end = periodEnd;
    if (vatType) params.vat_type = vatType;
    params.limit = 500;
    setLoading(true);
    api.get("/vat/audit-trail", { params }).then((r) => setEntries(r.data.entries || [])).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchEntries(); }, []);

  const totalOutput = entries.filter((e) => e.vat_type === "output").reduce((s, e) => s + (e.vat_amount || 0), 0);
  const totalInput = entries.filter((e) => e.vat_type === "input").reduce((s, e) => s + (e.vat_amount || 0), 0);

  const TYPE_ICONS = { invoice: Receipt, expense: CreditCard, credit_note: FileText };
  const TYPE_COLORS = { output: "bg-red-50 text-red-700", input: "bg-green-50 text-green-700" };

  return (
    <div className="animate-fade-in" data-testid="vat-audit-trail-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl text-[#1F2937] tracking-tight">VAT Audit Trail</h1>
          <p className="text-sm text-gray-500 mt-1">Complete record of all VAT transactions for SARS compliance</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="border border-gray-200 rounded-sm shadow-sm mb-5">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div><label className="text-[10px] font-semibold uppercase text-gray-500 block mb-1">Period Start</label>
              <Input data-testid="audit-period-start" type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className="rounded-sm h-9 w-40" /></div>
            <div><label className="text-[10px] font-semibold uppercase text-gray-500 block mb-1">Period End</label>
              <Input data-testid="audit-period-end" type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className="rounded-sm h-9 w-40" /></div>
            <div><label className="text-[10px] font-semibold uppercase text-gray-500 block mb-1">VAT Type</label>
              <Select value={vatType || "_all"} onValueChange={(v) => setVatType(v === "_all" ? "" : v)}>
                <SelectTrigger data-testid="audit-vat-type" className="rounded-sm h-9 w-36"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All Types</SelectItem>
                  <SelectItem value="output">Output (Sales)</SelectItem>
                  <SelectItem value="input">Input (Purchases)</SelectItem>
                </SelectContent>
              </Select></div>
            <Button data-testid="audit-search-btn" onClick={fetchEntries} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm h-9">
              <Search className="w-3.5 h-3.5 mr-1" /> Search
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        <Card className="border border-gray-200 rounded-sm shadow-sm"><CardContent className="p-4">
          <p className="text-[10px] font-semibold uppercase text-gray-500 mb-1">Total Transactions</p>
          <p className="font-heading font-bold text-2xl text-[#1F2937]">{entries.length}</p>
        </CardContent></Card>
        <Card className="border border-gray-200 rounded-sm shadow-sm"><CardContent className="p-4">
          <div className="flex items-center gap-1.5 mb-1"><ArrowUpRight className="w-3.5 h-3.5 text-[#E63946]" /><p className="text-[10px] font-semibold uppercase text-gray-500">Output VAT (Sales)</p></div>
          <p className="font-heading font-bold text-2xl text-[#E63946]">{formatCurrency(totalOutput)}</p>
        </CardContent></Card>
        <Card className="border border-gray-200 rounded-sm shadow-sm"><CardContent className="p-4">
          <div className="flex items-center gap-1.5 mb-1"><ArrowDownLeft className="w-3.5 h-3.5 text-green-600" /><p className="text-[10px] font-semibold uppercase text-gray-500">Input VAT (Purchases)</p></div>
          <p className="font-heading font-bold text-2xl text-green-600">{formatCurrency(totalInput)}</p>
        </CardContent></Card>
      </div>

      {/* Table */}
      <Card className="border border-gray-200 rounded-sm shadow-sm">
        <CardContent className="p-0">
          {loading ? <div className="animate-pulse h-48 bg-gray-50" /> : entries.length === 0 ? (
            <div className="p-10 text-center"><ShieldCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" /><p className="text-sm text-gray-500">No VAT transactions found for the selected period.</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="vat-audit-table">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-500 uppercase">Date</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-500 uppercase">Type</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-500 uppercase">Doc #</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-500 uppercase">Entity</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-500 uppercase">VAT #</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-500 uppercase">VAT Code</th>
                    <th className="px-3 py-2.5 text-right text-[10px] font-semibold text-gray-500 uppercase">Excl VAT</th>
                    <th className="px-3 py-2.5 text-right text-[10px] font-semibold text-gray-500 uppercase">VAT</th>
                    <th className="px-3 py-2.5 text-right text-[10px] font-semibold text-gray-500 uppercase">Incl VAT</th>
                    <th className="px-3 py-2.5 text-center text-[10px] font-semibold text-gray-500 uppercase">Direction</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e, i) => {
                    const Icon = TYPE_ICONS[e.transaction_type] || FileText;
                    return (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`audit-entry-${i}`}>
                        <td className="px-3 py-2 text-xs text-gray-600">{formatDate(e.transaction_date)}</td>
                        <td className="px-3 py-2"><div className="flex items-center gap-1.5"><Icon className="w-3.5 h-3.5 text-gray-400" /><span className="text-xs capitalize">{e.transaction_type}</span></div></td>
                        <td className="px-3 py-2 font-mono text-xs text-[#E63946]">{e.transaction_number}</td>
                        <td className="px-3 py-2 text-xs truncate max-w-[150px]">{e.entity_name}</td>
                        <td className="px-3 py-2 font-mono text-[10px] text-gray-500">{e.entity_vat_number || "-"}</td>
                        <td className="px-3 py-2"><span className="text-[10px] font-mono">{e.vat_code} ({e.vat_rate}%)</span></td>
                        <td className="px-3 py-2 text-right text-xs">{formatCurrency(e.amount_excl_vat)}</td>
                        <td className="px-3 py-2 text-right text-xs font-medium">{formatCurrency(e.vat_amount)}</td>
                        <td className="px-3 py-2 text-right text-xs">{formatCurrency(e.amount_incl_vat)}</td>
                        <td className="px-3 py-2 text-center"><Badge className={`${TYPE_COLORS[e.vat_type] || "bg-gray-100 text-gray-600"} rounded-full text-[9px] capitalize`}>{e.vat_type}</Badge></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
